import {TuiAxes} from '@taiga-ui/addon-charts/components/axes';
import {TuiBarChart} from '@taiga-ui/addon-charts/components/bar-chart';
import {TuiLegendItem} from '@taiga-ui/addon-charts/components/legend-item';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {CdkScrollable} from '@angular/cdk/scrolling';
import {DatePipe, PercentPipe, registerLocaleData} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import {CKEditorModule} from '@ckeditor/ckeditor5-angular';
import {bootstrapPublicSettings} from '@gatewee/services/core/app.settings';
import {PublicSettings} from '@gatewee/types/system';
import {NgbModule, NgbTooltipModule} from '@ng-bootstrap/ng-bootstrap';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {LightgalleryModule} from 'lightgallery/angular';
import {NgApexchartsModule} from 'ng-apexcharts';
import {NgOtpInputComponent} from 'ng-otp-input';
import {Select2Module} from 'ng-select2-component';
import {TagInputModule} from 'ngx-chips';
import {ColorPickerModule} from 'ngx-color-picker';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {NgxMaterialTimepickerModule} from 'ngx-material-timepicker';
import {CarouselModule} from 'ngx-owl-carousel-o';
import {NgxSpinnerModule} from 'ngx-spinner';
import {OverlayContainer} from '@angular/cdk/overlay';
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';

// Material UI --
import {MaterialModule} from '@gatewee/material';

// Versão de Lib (1)
import {ComponentsV1Module} from '@gatewee/components';

// Versão de Lib (2)
import {ComponentsV2Module} from '@gatewee/components';

// Libs
import {ServicesModule} from '@gatewee/services';
import {APIInterceptor} from '@gatewee/services/interceptors/api.interceptor';
import {UtilsModule} from '@gatewee/utils';
import {GwCountUpDirective} from '@gatewee/utils/directives/count-up.directive';
// import {ModalOverlayContainer} from '@gatewee/utils/helpers/modal-overlay-container';

// Pipes
import {OriginStatusPipe} from '../utils/origin-status.pipe';
import {RecipientStatusPipe} from '../utils/recipient-status.pipe';
import {RelativeTimePipe} from '../utils/relative-time.pipe';
import {UserStatusPipe} from '../utils/user-status.pipe';
// Services
import {BrandingService} from '@gatewee/services/core/branding.service';
import {AuthService} from './_services/auth/auth.service';
import {LanguageService} from './_services/language/language.service';

// Elements
import {JsonModalComponent} from './elements/json-modal/json-modal.component';
import {AppLinksComponent} from './elements/app-links/app-links.component';

// Layouts
import {AdminComponent} from './layout/admin/admin.component';

// Directives
import {ClickOutsideDirective} from './directives/click-outside.directive';

// Pages
import {LockScreenComponent} from './pages/authentication/lock-screen/lock-screen.component';
import {LoginComponent} from './pages/authentication/login/login.component';
import {OnboardingKycComponent} from './pages/authentication/onboarding-kyc/onboarding-kyc.component';
import {OnboardingSuccessComponent} from './pages/authentication/onboarding-success/onboarding-success.component';
import {OnboardingModule} from './pages/authentication/onboarding/onboarding.module';
import {PasswordResetRequestComponent} from './pages/authentication/password-reset-request/password-reset-request.component';
import {PasswordResetComponent} from './pages/authentication/password-reset/password-reset.component';
import {SignupComponent} from './pages/authentication/signup/signup.component';
import {DashboardComponent} from './pages/dashboard/dashboard.component';
import {DocumentationComponent} from './pages/documentation/documentation.component';
import {HomeComponent} from './pages/home/home.component';
import {MyFeesComponent} from './pages/my-fees/my-fees.component';
import {RecipientKeysComponent} from './pages/recipient-keys/recipient-keys.component';
import {RecipientPixKeysComponent} from './pages/recipient-pix-keys/recipient-pix-keys.component';
import {UsersComponent} from './pages/users/users.component';
import {WhatsappQrCodeDialogComponent} from './pages/whatsapp-settings/dialogs/whatsapp-qr-code-dialog.component';
import {WhatsappTestMessageDialogComponent} from './pages/whatsapp-settings/dialogs/whatsapp-test-message-dialog.component';
import {WhatsappSettingsComponent} from './pages/whatsapp-settings/whatsapp-settings.component';
import {WithdrawalPixFormModule} from './pages/withdrawals/form/withdrawal-pix-form.module';
// Outros
import {publicSettings} from '@gatewee/services/core/settings.service';

import {provideEventPlugins} from '@taiga-ui/event-plugins';

// Icons
import {BrowserModule} from '@angular/platform-browser';
import {MobileBridgeService} from '@gatewee/services/core/mobile-bridge.service';
import {OverlayInsideTuiRootContainer} from '@gatewee/utils/helpers/overlay-insider-tui';
import {
  TUI_DATE_FORMAT,
  TUI_MONTHS,
  TUI_SHORT_WEEK_DAYS,
  TuiRoot,
} from '@taiga-ui/core';
import {tuiNumberFormatProvider} from '@taiga-ui/core/tokens';
import {of} from 'rxjs';
registerLocaleData(localePt, 'pt');

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

export function initLang(languageService: LanguageService) {
  return () => languageService.initLanguage();
}

@NgModule({
  declarations: [
    ClickOutsideDirective,
    AppComponent,
    AdminComponent,
    RecipientStatusPipe,
    DashboardComponent,
    RelativeTimePipe,
    UsersComponent,
    SignupComponent,
    RecipientKeysComponent,
    UserStatusPipe,
    PasswordResetComponent,
    PasswordResetRequestComponent,
    LoginComponent,
    LockScreenComponent,
    WhatsappSettingsComponent,
    DocumentationComponent,
    OnboardingKycComponent,
    OnboardingSuccessComponent,
    RecipientPixKeysComponent,
    OriginStatusPipe,
    HomeComponent,
    MyFeesComponent,
    AppLinksComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'pt',
    }),
    CarouselModule,
    BrowserAnimationsModule,
    NgxMaskDirective,
    NgxSpinnerModule.forRoot({type: 'ball-scale-multiple'}),
    NgbModule,
    NgApexchartsModule,
    FormsModule,
    ReactiveFormsModule,
    CKEditorModule,
    DragDropModule,
    TagInputModule,
    LightgalleryModule,
    ColorPickerModule,
    NgxMaterialTimepickerModule,
    Select2Module,
    NgbTooltipModule,
    MaterialModule,
    DatePipe,
    CdkScrollable,
    PercentPipe,
    WithdrawalPixFormModule,
    NgOtpInputComponent,
    JsonModalComponent,
    ComponentsV1Module,
    ComponentsV2Module,
    UtilsModule,
    GwCountUpDirective,
    ServicesModule,
    TuiRoot,
    WhatsappQrCodeDialogComponent,
    WhatsappTestMessageDialogComponent,
    OnboardingModule,
    TuiAxes,
    TuiBarChart,
    TuiLegendItem,
  ],
  providers: [
    AuthService,
    {provide: 'BASE_URL', useValue: environment.API_URL},
    {provide: 'SCOPE', useValue: 'recipient'},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: APIInterceptor,
      multi: true,
    },
    BrandingService,
    provideNgxMask(),
    {provide: LOCALE_ID, useValue: 'pt'},
    MobileBridgeService,
    provideHttpClient(),
    {
      provide: publicSettings,
      useFactory: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const win = window as any;
        return (win.__PUBLIC_SETTINGS__ ?? {}) as PublicSettings;
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: bootstrapPublicSettings,
      deps: [HttpClient, BrandingService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initLang,
      deps: [LanguageService],
      multi: true,
    },
    provideAnimations(),
    provideEventPlugins(),
    {
      provide: TUI_MONTHS,
      useFactory: () =>
        of([
          'Janeiro',
          'Fevereiro',
          'Março',
          'Abril',
          'Maio',
          'Junho',
          'Julho',
          'Agosto',
          'Setembro',
          'Outubro',
          'Novembro',
          'Dezembro',
        ]),
    },
    {
      provide: TUI_SHORT_WEEK_DAYS,
      useFactory: () => of(['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']),
    },
    {
      provide: TUI_DATE_FORMAT,
      useValue: of({
        separator: '/',
        mode: 'DMY',
      }),
    },
    tuiNumberFormatProvider({
      precision: 2,
      decimalSeparator: ',',
      thousandSeparator: '.',
      rounding: 'round',
      decimalMode: 'pad',
    }),
    {
      provide: OverlayContainer,
      useClass: OverlayInsideTuiRootContainer,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
